/**
 * Language Context Provider
 * Provides translation context to the app
 */

import React, { useContext, ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslationProvider, TranslationContext } from '../hooks/useTranslation';

interface LanguageProviderProps {
  children: ReactNode;
  botUsername: string;
  defaultLanguage?: string;
}

/**
 * Language Provider Component
 * Wrap your app with this to enable translations
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  botUsername,
  defaultLanguage = 'en'
}) => {
  const translationValue = useTranslationProvider(botUsername, defaultLanguage);

  return (
    <TranslationContext.Provider value={translationValue}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Language Selector Component
 * Dropdown to change language
 */
interface LanguageSelectorProps {
  variant?: 'dropdown' | 'list' | 'compact';
  showFlags?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'dropdown',
  showFlags = true
}) => {
  const context = useContext(TranslationContext);

  if (!context) return null;

  const { language, setLanguage, availableLanguages } = context;
  const currentLang = availableLanguages.find(l => l.code === language);

  if (variant === 'compact') {
    return (
      <CompactSelector>
        <CurrentFlag>{currentLang?.flag || '🌐'}</CurrentFlag>
        <Select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          {availableLanguages.map(lang => (
            <option key={lang.code} value={lang.code}>
              {showFlags ? `${lang.flag} ` : ''}{lang.name}
            </option>
          ))}
        </Select>
      </CompactSelector>
    );
  }

  if (variant === 'list') {
    return (
      <LanguageList>
        {availableLanguages.map(lang => (
          <LanguageItem
            key={lang.code}
            $active={lang.code === language}
            onClick={() => setLanguage(lang.code)}
          >
            {showFlags && <LangFlag>{lang.flag}</LangFlag>}
            <LangName>{lang.name}</LangName>
            {lang.code === language && (
              <CheckIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </CheckIcon>
            )}
          </LanguageItem>
        ))}
      </LanguageList>
    );
  }

  // Default dropdown
  return (
    <DropdownContainer>
      <DropdownLabel>Language</DropdownLabel>
      <DropdownSelect
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
      >
        {availableLanguages.map(lang => (
          <option key={lang.code} value={lang.code}>
            {showFlags ? `${lang.flag} ` : ''}{lang.name}
          </option>
        ))}
      </DropdownSelect>
    </DropdownContainer>
  );
};

// Styled components
const CompactSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
`;

const CurrentFlag = styled.span`
  font-size: 20px;
`;

const Select = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const LanguageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LanguageItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)15'
    : 'var(--tg-theme-bg-color, #ffffff)'};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;

  &:active {
    background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  }
`;

const LangFlag = styled.span`
  font-size: 24px;
`;

const LangName = styled.span`
  flex: 1;
  font-size: 15px;
  color: var(--tg-theme-text-color, #000000);
`;

const CheckIcon = styled.div`
  color: var(--tg-theme-button-color, #3390ec);
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DropdownLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
`;

const DropdownSelect = styled.select`
  padding: 12px;
  background: var(--tg-theme-bg-color, #ffffff);
  border: 1px solid var(--tg-theme-hint-color, #e0e0e0)60;
  border-radius: 10px;
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: var(--tg-theme-button-color, #3390ec);
  }
`;

export default LanguageProvider;
